// HelpButtonSeen — 「使い方」ボタンの「New!」バッジを「初回だけ」出すための
// seen フラグ永続化 (infrastructure 層)。
//
// 背景 (作者フィードバック 2026-09-22「もっと目立つようにできないかな」):
//   - Classic 上部バー / Modern ヘッダ / スマホ「その他」シートの 3 入口すべてが
//     使い方マニュアルへの導線として新規機能訴求の「New!」バッジを出す。常時 New! だと
//     陳腐化するので **初回に入口を押したら消す** (以後は非表示)。
//   - 既存の New! バッジ機構 (`pastCanvasSeen.ts` / `timelapseShareSeen.ts` /
//     `customCursorNewBadgeSeen.ts`) と**そっくり複製**し、別 localStorage key で
//     独立管理する。
//   - Classic / Modern (デスクトップ・スマホ) 全入口で同じ store key を参照するので、
//     どの入口で押しても全ての入口のバッジが消える。
//
// 設計 (`customCursorNewBadgeSeen.ts` と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は「未 seen」
//     (= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: helpButtonSeen.test.ts (storage-injection style)。

/** localStorage キー: 使い方ボタンの New! バッジを消したか (= 初回に入口を押したか). */
export const HELP_BUTTON_SEEN_KEY = 'oekaki:help-button-seen';

/** Minimal Storage shape this module uses (customCursorNewBadgeSeen.ts と同流儀). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface HelpButtonSeenStore {
  /** 既に New! バッジを消したか (= 初回に入口を押したか). 不在 / storage 不可 → false. */
  hasSeen(): boolean;
  /** 初回に入口を押したことを記録 (= 以後バッジ非表示). storage 不可なら no-op. */
  markSeen(): void;
  /** key を削除. テスト / リセット用. storage 不可なら no-op. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:help-button-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateHelpButtonSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `HelpButtonSeenStore` のインスタンスを構築. storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する.
 */
export const createHelpButtonSeenStore = (
  opts: CreateHelpButtonSeenStoreOptions = {},
): HelpButtonSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(HELP_BUTTON_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(HELP_BUTTON_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(HELP_BUTTON_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
